import type { ReactNode } from "react";
import { useAdminDiagnostics } from "../hooks/useAdminData";
import type { AdminDiagnostics } from "@/lib/types";
import { AdminPageHeader } from "../components/AdminPageHeader";

/**
 * M8a A1 / A2 / A3 / A4 — `/admin/overview`.
 *
 * Six KPI tiles + Recent Activity feed. Reads the same
 * `useAdminDiagnostics` query the shell status strip uses, so this
 * page does NOT trigger a second fetch — it just subscribes to the
 * already-running 10 s loop.
 */
export function OverviewPage() {
  const diag = useAdminDiagnostics();
  const d = diag.data;

  return (
    <div data-testid="admin-overview" className="flex flex-col gap-4">
      <AdminPageHeader
        eyebrow="Operations"
        title="Overview"
        description="Monitor platform health, storage headroom, video processing state, and recent operator activity."
        meta={diag.isFetching ? "Refreshing…" : "Auto-refreshes every 10 s"}
      />

      {diag.error ? (
        <p
          data-testid="admin-overview-error"
          className="text-destructive font-mono text-xs"
        >
          Failed to load admin diagnostics.
        </p>
      ) : null}

      {/* A1 — Six KPI tiles */}
      <div
        data-testid="admin-overview-kpis"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
      >
        <KpiTile
          testid="kpi-disk"
          label="Free Disk"
          value={d ? formatBytes(d.disk?.free ?? 0) : "—"}
          sub={d ? `min ${formatBytes(d.disk?.min_free_bytes ?? 0)}` : ""}
          // #212 — fail-safe tone. The previous logic mapped only on
          // the backend's `enough_space` boolean; a 0 B reading with
          // `enough_space: true` (the dev container's known-spurious
          // state, or a stale/unreliable backend value) rendered as the
          // good token, which is a danger condition reading as healthy.
          // Front-end now downgrades to `bad` when `free` is at or
          // below the configured minimum (or unknown), so the tile
          // fails safe regardless of the source-value reliability.
          tone={diskTone(d?.disk)}
        />
        <KpiTile
          testid="kpi-headroom"
          label="Upload Headroom"
          value={d?.disk?.enough_space ? "OK" : "LOW"}
          tone={diskTone(d?.disk)}
        />
        <KpiTile
          testid="kpi-matches"
          label="Matches"
          value={d ? String(d.counts.matches) : "—"}
          sub={
            d
              ? `${d.counts.ready_slots} ready · ${d.counts.transcoding_slots} transcoding`
              : ""
          }
        />
        <KpiTile
          testid="kpi-failed"
          label="Failed Slots"
          value={d ? String(d.counts.failed_slots) : "—"}
          tone={(d?.counts.failed_slots ?? 0) > 0 ? "bad" : "good"}
        />
        <KpiTile
          testid="kpi-hls-backfill"
          label="HLS Backfill"
          value={d?.hls.backfill_running ? "Running" : "Idle"}
          sub={
            d ? `${d.counts.hls_missing_slots} slots missing HLS` : ""
          }
          tone={d?.hls.backfill_running ? "busy" : "idle"}
        />
        <KpiTile
          testid="kpi-chunk"
          label="Chunk Size"
          value={
            d
              ? formatBytes(d.upload_limits.chunk_size_bytes)
              : "—"
          }
          sub={
            d
              ? `${d.upload_limits.stale_upload_session_seconds}s session timeout`
              : ""
          }
        />
      </div>

      {/* A2 — Recent Activity */}
      <ActivityFeed payload={d} />
    </div>
  );
}

type Tone = "good" | "warn" | "bad" | "busy" | "idle" | "muted";

/** Fail-safe tone for the Free Disk / Upload Headroom KPI tiles (#212).
 *
 * The previous logic trusted the backend's `enough_space` boolean: a
 * 0-bytes-free reading with `enough_space: true` (the dev container's
 * known-spurious state, or a stale value from a backend that hasn't
 * polled the disk recently) rendered as the GOOD token. A 0 B reading
 * against any nonzero minimum is a danger condition — it must not
 * read as healthy regardless of source-value reliability.
 *
 * Mapping:
 *   - free unknown OR free <= min_free_bytes → BAD (red)
 *   - enough_space false (with a positive free)  → WARN (orange)
 *   - enough_space true                          → GOOD (green)
 */
export function diskTone(
  disk: AdminDiagnostics["disk"] | undefined,
): Tone {
  if (!disk) return "muted";
  const free = disk.free;
  const min = disk.min_free_bytes;
  // Defensive: anything we can't interpret defaults to BAD.
  if (typeof free !== "number" || !Number.isFinite(free)) return "bad";
  // Treat at-or-below-minimum as the danger condition even if the
  // backend disagrees. This is the explicit ask in #212.
  if (free <= (typeof min === "number" ? min : 0)) return "bad";
  return disk.enough_space ? "good" : "warn";
}

function KpiTile({
  label,
  value,
  sub,
  tone = "muted",
  testid,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: Tone;
  testid: string;
}): ReactNode {
  const TONE_CLASSES: Record<Tone, string> = {
    good: "text-good",
    warn: "text-accent",
    bad: "text-destructive",
    busy: "text-accent",
    idle: "text-ink-muted",
    muted: "text-ink",
  };
  return (
    <div
      data-testid={testid}
      data-tone={tone}
      className="flex flex-col gap-1 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <span className={`text-lg font-mono ${TONE_CLASSES[tone]}`}>{value}</span>
      {sub ? (
        <span className="text-xs font-mono text-ink-muted">{sub}</span>
      ) : null}
    </div>
  );
}

function ActivityFeed({
  payload,
}: {
  payload: AdminDiagnostics | undefined;
}): ReactNode {
  const events = (payload?.recent_activity ?? []).slice(0, 8);
  return (
    <section
      data-testid="admin-overview-activity"
      className="flex flex-col gap-2 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <header className="flex items-baseline justify-between">
        <h3 className="text-sm font-display uppercase tracking-wider text-ink">
          Recent Activity
        </h3>
        <span className="text-[0.6rem] font-mono text-ink-muted">
          {payload ? `${payload.recent_activity?.length ?? 0} total` : ""}
        </span>
      </header>
      {events.length === 0 ? (
        <p
          data-testid="admin-overview-activity-empty"
          className="text-xs font-mono text-ink-muted"
        >
          No activity in the last 72 hours.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {events.map((e, i) => (
            <li
              key={`${e.timestamp}-${i}`}
              data-testid="admin-activity-row"
              data-activity-severity={e.severity ?? "info"}
              className="flex items-start gap-2 rounded border border-rule/60 bg-elevated px-2 py-1.5"
            >
              <span className="text-[0.6rem] font-mono text-ink-muted shrink-0 mt-0.5">
                {formatTime(e.timestamp)}
              </span>
              <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted shrink-0 mt-0.5">
                {e.event_type}
              </span>
              <span className="text-xs text-ink grow break-words">
                {e.message ?? "—"}
                {e.actor ? (
                  <span className="text-ink-muted"> · {e.actor}</span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Compact byte formatter — "12.3 GB" / "256 MB" / "4 KB". */
function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

/** Reads ISO timestamp / activity timestamp; falls back to raw on parse fail. */
function formatTime(ts: string | undefined | null): string {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  const HH = String(d.getHours()).padStart(2, "0");
  const MM = String(d.getMinutes()).padStart(2, "0");
  return `${HH}:${MM}`;
}
